import * as R from 'remeda';
import {
  isSchemaDatapoint,
  isSchemaDatapointButton,
  isSchemaDatapointDate,
  isSchemaDatapointEnum,
  isSchemaDatapointNumber,
  isSchemaDatapointString,
  isSchemaSection,
  isSchemaSimpleMultiValue,
  isSchemaTableMultiValue,
  SchemaDatapoint,
  SchemaDatapointButton,
  SchemaDatapointDate,
  SchemaDatapointEnum,
  SchemaDatapointNumber,
  SchemaDatapointString,
  SchemaItem,
  SchemaSection,
  SchemaSimpleMultivalue,
  SchemaTableMultivalue,
  SchemaUiConfigType,
} from '@rossum/api-client/schemas';
import { preDefinedDescriptions } from '../../constants';
import { emptyDefaultValues, FieldsFormValues } from '../formModels';
import { PropertyEvolver } from './toFormValues.utils';
import { Evolver, isLineItemChild } from './utils';

type NonButtonDatapoint =
  | SchemaDatapointString
  | SchemaDatapointNumber
  | SchemaDatapointDate
  | SchemaDatapointEnum;

type SchemaSimpleMultivalueWithNonButtonDatapoint = SchemaSimpleMultivalue & {
  children: NonButtonDatapoint;
};

const isNonButtonDatapoint = (item: unknown): item is NonButtonDatapoint =>
  isSchemaDatapoint(item) && !isSchemaDatapointButton(item);

const isSchemaSimpleMultiValueWithNonButtonDatapoint = (
  item: unknown
): item is SchemaSimpleMultivalueWithNonButtonDatapoint =>
  isSchemaSimpleMultiValue(item) && isNonButtonDatapoint(item.children);

type ManualDatapoint = NonButtonDatapoint & {
  uiConfiguration: { type: 'manual' };
};

const isManualField = (item: unknown): item is ManualDatapoint =>
  isNonButtonDatapoint(item) && item.uiConfiguration?.type === 'manual';

type CapturedDatapoint = NonButtonDatapoint & {
  uiConfiguration: {
    type: 'captured';
  };
};

const isCapturedField = (item: unknown): item is CapturedDatapoint =>
  isNonButtonDatapoint(item) && item.uiConfiguration?.type === 'captured';

type FormulaDatapoint = NonButtonDatapoint & {
  uiConfiguration: { type: 'formula' };
};

const isFormulaField = (item: unknown): item is FormulaDatapoint =>
  isNonButtonDatapoint(item) && item.uiConfiguration?.type === 'formula';

type DataDatapoint = NonButtonDatapoint & { uiConfiguration: { type: 'data' } };

const isDataField = (item: unknown): item is DataDatapoint =>
  isNonButtonDatapoint(item) && item.uiConfiguration?.type === 'data';

type UnsetDatapoint = NonButtonDatapoint & {
  uiConfiguration: { type: null | undefined };
};

type ReasoningDatapoint = NonButtonDatapoint & {
  uiConfiguration: { type: 'reasoning' };
};

const isReasoningField = (item: unknown): item is ReasoningDatapoint =>
  isNonButtonDatapoint(item) && item.uiConfiguration?.type === 'reasoning';

type LookupDatapoint = NonButtonDatapoint & {
  uiConfiguration: { type: 'lookup' };
};

const isLookupField = (item: unknown): item is LookupDatapoint =>
  isNonButtonDatapoint(item) && item.uiConfiguration?.type === 'lookup';

const isUnsetField = (item: unknown): item is UnsetDatapoint =>
  isNonButtonDatapoint(item) &&
  (item.uiConfiguration?.type === undefined ||
    item.uiConfiguration?.type === null);

const isComputedField = (
  item: unknown
): item is FormulaDatapoint | ReasoningDatapoint | LookupDatapoint =>
  isFormulaField(item) || isReasoningField(item) || isLookupField(item);

const isValidType = (type: unknown): type is SchemaUiConfigType =>
  type === 'captured' ||
  type === 'data' ||
  type === 'manual' ||
  type === 'formula' ||
  type === 'reasoning' ||
  type === 'lookup';

const evolveFieldType: PropertyEvolver<'fieldType'> =
  (item, schemaPath) => def => {
    if (isLineItemChild(schemaPath)(item)) {
      if (isSchemaDatapointButton(item)) {
        return 'lineItemButton';
      }
      return 'lineItemSimpleValue';
    }
    if (isSchemaSection(item)) {
      return 'section';
    }
    if (isSchemaTableMultiValue(item)) {
      return 'lineItems';
    }
    if (isSchemaSimpleMultiValue(item)) {
      return 'multivalue';
    }
    if (isSchemaDatapointButton(item)) {
      return 'button';
    }
    if (isSchemaDatapoint(item)) {
      return 'simpleValue';
    }
    return def;
  };

const extractId =
  (defaultValue: FieldsFormValues['field']['id']) =>
  (item: SchemaItem | SchemaSection) => {
    if (isSchemaSimpleMultiValue(item)) {
      return item.children?.id ?? defaultValue;
    }
    return item.id ?? defaultValue;
  };

const evolveId: PropertyEvolver<'field.id'> = item => def => {
  if (item) {
    return extractId(def)(item);
  }
  return def;
};

const extractLabel =
  (defaultValue: FieldsFormValues['field']['label']) =>
  (item: SchemaItem | SchemaSection) =>
    item.label ?? defaultValue;

const evolveLabel: PropertyEvolver<'field.label'> = item => def => {
  if (item) {
    return extractLabel(def)(item);
  }
  return def;
};

const extractDescription =
  (defaultValue: FieldsFormValues['field']['description']) =>
  (item: SchemaItem | SchemaSection) => {
    const customDescription = item.id
      ? preDefinedDescriptions[item.id as keyof typeof preDefinedDescriptions]
      : undefined;

    return item.description ?? customDescription ?? defaultValue;
  };

const evolveDescription: PropertyEvolver<'field.description'> = item => def => {
  if (item) {
    return extractDescription(def)(item);
  }
  return def;
};

const extractHidden =
  (defaultValue: FieldsFormValues['field']['hidden']) =>
  (item: SchemaItem | SchemaSection) =>
    item.hidden ?? defaultValue;

const evolveHidden: PropertyEvolver<'field.hidden'> = item => def => {
  if (item) {
    return extractHidden(def)(item);
  }
  return def;
};

const extractExported =
  (defaultValue: FieldsFormValues['field']['exported']) =>
  (item: NonButtonDatapoint) =>
    item.canExport ?? defaultValue;

const evolveExported: PropertyEvolver<'field.exported'> = item => def => {
  if (isNonButtonDatapoint(item)) {
    return extractExported(def)(item);
  }
  if (isSchemaSimpleMultiValueWithNonButtonDatapoint(item)) {
    return extractExported(def)(item.children);
  }
  if (isSchemaTableMultiValue(item)) {
    return false;
  }
  return def;
};

const extractSimpleValue = (item: SchemaItem | SchemaSection | null) => {
  if (isSchemaSimpleMultiValue(item)) {
    return item.children;
  }
  if (isSchemaDatapoint(item)) {
    return item;
  }
  return null;
};

const extractRequired =
  (defaultValue: FieldsFormValues['field']['required']) =>
  (item: NonButtonDatapoint) =>
    item.constraints?.required ?? defaultValue;

const evolveRequired: PropertyEvolver<'field.required'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isNonButtonDatapoint(simpleValue)) {
    return extractRequired(def)(simpleValue);
  }
  return def;
};

const extractType =
  (defaultValue: FieldsFormValues['field']['valueSource']) =>
  (
    schemaItem: NonButtonDatapoint
  ): FieldsFormValues['field']['valueSource'] => {
    const type = schemaItem.uiConfiguration?.type;
    if (isValidType(type)) {
      return type;
    }
    if (type === null || type === undefined) {
      return 'unset';
    }
    return defaultValue;
  };

const evolveValueSource: PropertyEvolver<'field.valueSource'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isNonButtonDatapoint(simpleValue)) {
    return extractType(def)(simpleValue);
  }
  return def;
};

const extractEditing =
  (defaultValue: FieldsFormValues['field']['editing']) =>
  (item: NonButtonDatapoint) =>
    item.uiConfiguration?.edit ?? defaultValue;

const resolveEditing =
  (defaultValue: FieldsFormValues['field']['editing']) =>
  (item: NonButtonDatapoint) => {
    if (isManualField(item)) {
      return 'enabled';
    }
    if (
      isDataField(item) ||
      isCapturedField(item) ||
      isFormulaField(item) ||
      isReasoningField(item) ||
      isLookupField(item)
    ) {
      return extractEditing(defaultValue)(item);
    }
    return defaultValue;
  };

const evolveEditing: PropertyEvolver<'field.editing'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isNonButtonDatapoint(simpleValue)) {
    return resolveEditing(def)(simpleValue);
  }
  return def;
};

const extractAiEngineFields =
  (defaultValue: FieldsFormValues['field']['aiEngineFields']) =>
  (item: NonButtonDatapoint | SchemaTableMultivalue) =>
    item.rirFieldNames ?? defaultValue;

const resolveAiEngineFields =
  (defaultValue: FieldsFormValues['field']['aiEngineFields']) =>
  (item: NonButtonDatapoint) => {
    if (isCapturedField(item) || isDataField(item) || isUnsetField(item)) {
      return extractAiEngineFields(defaultValue)(item);
    }
    return defaultValue;
  };

const evolveAiEngineFields: PropertyEvolver<'field.aiEngineFields'> =
  item => def => {
    if (isSchemaTableMultiValue(item)) {
      return extractAiEngineFields(def)(item);
    }
    if (isSchemaSimpleMultiValue(item) || isSchemaDatapoint(item)) {
      const simpleValue = extractSimpleValue(item);
      if (isNonButtonDatapoint(simpleValue)) {
        return resolveAiEngineFields(def)(simpleValue);
      }
      return def;
    }
    return def;
  };

const nonNegativeNumericStringOr = (defaultValue: string) => (val: number) =>
  val < 0 ? defaultValue : String(val);

const extractThreshold =
  (defaultValue: FieldsFormValues['field']['scoreThreshold']) =>
  (item: NonButtonDatapoint) => {
    const threshold = item.scoreThreshold ?? -1;
    return nonNegativeNumericStringOr(defaultValue)(threshold);
  };

const resolveThreshold =
  (defaultValue: FieldsFormValues['field']['scoreThreshold']) =>
  (item: NonButtonDatapoint) =>
    extractThreshold(defaultValue)(item);

const evolveThreshold: PropertyEvolver<'field.scoreThreshold'> =
  item => def => {
    const simpleValue = extractSimpleValue(item);
    if (isNonButtonDatapoint(simpleValue)) {
      return resolveThreshold(def)(simpleValue);
    }
    return def;
  };

const extractDataType =
  (defaultValue: FieldsFormValues['field']['dataType']) =>
  (item: NonButtonDatapoint) =>
    item.type ?? defaultValue;

const evolveDataType: PropertyEvolver<'field.dataType'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isLookupField(simpleValue)) {
    return 'enum' as const;
  }

  if (isNonButtonDatapoint(simpleValue)) {
    return extractDataType(def)(simpleValue);
  }
  return def;
};

const extractMinLength =
  (defaultValue: FieldsFormValues['field']['minLength']) =>
  (item: SchemaDatapointString) => {
    const min = item.constraints?.length?.min ?? -1;
    return nonNegativeNumericStringOr(defaultValue)(min);
  };

const evolveMinLength: PropertyEvolver<'field.minLength'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isSchemaDatapointString(simpleValue)) {
    return extractMinLength(def)(simpleValue);
  }
  return def;
};

const extractMaxLength =
  (defaultValue: FieldsFormValues['field']['maxLength']) =>
  (item: SchemaDatapointString) => {
    const max = item.constraints?.length?.max ?? -1;
    return nonNegativeNumericStringOr(defaultValue)(max);
  };

const evolveMaxLength: PropertyEvolver<'field.maxLength'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isSchemaDatapointString(simpleValue)) {
    return extractMaxLength(def)(simpleValue);
  }
  return def;
};

const extractExactLength =
  (defaultValue: FieldsFormValues['field']['exactLength']) =>
  (item: SchemaDatapointString) => {
    const exact = item.constraints?.length?.exact ?? -1;
    return nonNegativeNumericStringOr(defaultValue)(exact);
  };

const evolveExactLength: PropertyEvolver<'field.exactLength'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isSchemaDatapointString(simpleValue)) {
    return extractExactLength(def)(simpleValue);
  }
  return def;
};

const extractRegex =
  (defaultValue: FieldsFormValues['field']['regex']) =>
  (item: SchemaDatapointString) =>
    item.constraints?.regexp?.pattern ?? defaultValue;

const evolveRegex: PropertyEvolver<'field.regex'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isSchemaDatapointString(simpleValue)) {
    return extractRegex(def)(simpleValue);
  }
  return def;
};

const extractOptions =
  (defaultValue: FieldsFormValues['field']['options']) =>
  (item: SchemaDatapointEnum) =>
    item.options ?? defaultValue;

const evolveOptions: PropertyEvolver<'field.options'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isSchemaDatapointEnum(simpleValue)) {
    return extractOptions(def)(simpleValue);
  }
  return def;
};

const extractFormula =
  (defaultValue: FieldsFormValues['field']['formula']) =>
  (item: FormulaDatapoint) =>
    item.formula ?? defaultValue;

const evolveFormula: PropertyEvolver<'field.formula'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isFormulaField(simpleValue)) {
    return extractFormula(def)(simpleValue);
  }
  return def;
};

const extractPrompt =
  (defaultValue: FieldsFormValues['field']['prompt']) =>
  (item: ReasoningDatapoint) =>
    item.prompt ?? defaultValue;

const evolvePrompt: PropertyEvolver<'field.prompt'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isReasoningField(simpleValue)) {
    return extractPrompt(def)(simpleValue);
  }
  return def;
};

const extractMemory =
  (defaultValue: FieldsFormValues['field']['memory']) =>
  (item: SchemaDatapoint) =>
    R.pipe(item, R.pathOr(['memory'] as const, defaultValue ?? {}));

const evolveMemory: PropertyEvolver<'field.memory'> = item => def =>
  R.pipe(
    item,
    extractSimpleValue,
    R.conditional(
      [isComputedField, data => extractMemory(def)(data)],
      R.constant(def)
    )
  );

const extractMatching =
  (defaultValue: FieldsFormValues['field']['matching']) =>
  (item: LookupDatapoint) =>
    R.pipe(item, R.pathOr(['matching'] as const, defaultValue));

const evolveMatching: PropertyEvolver<'field.matching'> = item => def =>
  R.pipe(
    item,
    extractSimpleValue,
    R.conditional(
      [isLookupField, data => extractMatching(def)(data)],
      R.constant(def)
    )
  );

const extractContext =
  (defaultValue: FieldsFormValues['field']['context']) =>
  (item: ReasoningDatapoint) =>
    item.context ?? defaultValue;

const evolveContext: PropertyEvolver<'field.context'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (isReasoningField(simpleValue)) {
    return extractContext(def)(simpleValue);
  }
  return def;
};

const extractFormat =
  (defaultValue: FieldsFormValues['field']['format']) =>
  (item: SchemaDatapointNumber | SchemaDatapointDate) =>
    item.format ?? defaultValue;

const evolveFormat: PropertyEvolver<'field.format'> = item => def => {
  const simpleValue = extractSimpleValue(item);
  if (
    isSchemaDatapointNumber(simpleValue) ||
    isSchemaDatapointDate(simpleValue)
  ) {
    return extractFormat(def)(simpleValue);
  }
  return def;
};

const extractUrl =
  (defaultValue: FieldsFormValues['field']['url']) =>
  (item: SchemaDatapointButton) =>
    item.popupUrl ?? defaultValue;

const evolveUrl: PropertyEvolver<'field.url'> = item => def => {
  if (isSchemaDatapointButton(item)) {
    return extractUrl(def)(item);
  }
  return def;
};

const makeEvolver = (
  item: SchemaSection | SchemaItem | null,
  schemaPath?: SchemaPath | null
): Evolver<FieldsFormValues> => {
  return {
    fieldType: evolveFieldType(item, schemaPath),
    field: {
      id: evolveId(item),
      label: evolveLabel(item),
      description: evolveDescription(item),
      hidden: evolveHidden(item),
      exported: evolveExported(item),
      required: evolveRequired(item),
      valueSource: evolveValueSource(item),
      editing: evolveEditing(item),
      aiEngineFields: evolveAiEngineFields(item),
      scoreThreshold: evolveThreshold(item),
      dataType: evolveDataType(item),
      minLength: evolveMinLength(item),
      maxLength: evolveMaxLength(item),
      exactLength: evolveExactLength(item),
      regex: evolveRegex(item),
      options: evolveOptions(item),
      formula: evolveFormula(item),
      prompt: evolvePrompt(item),
      matching: evolveMatching(item),
      context: evolveContext(item),
      format: evolveFormat(item),
      url: evolveUrl(item),
      memory: evolveMemory(item),
    },
  };
};

type SchemaPath = Array<string | number>;
// converts a given API model into default values for the form
export const toFormValues = (
  apiModel: SchemaSection | SchemaItem | null,
  schemaPath?: SchemaPath | null
): FieldsFormValues =>
  R.pipe(emptyDefaultValues, R.evolve(makeEvolver(apiModel, schemaPath)));
